// BUG-2026-0120 #1 — Name-only quick-add of a client from inside the quote editor.
// Full contact/email/phone can be filled later on the Clients page.
// Duplicate handling: warn (via shared DuplicateWarning) but allow.
import { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material';
import { DuplicateWarning } from '../../../components/forms/DuplicateWarning';
import { createClient } from '../../../services/clientService';
import { apiErrorText, apiErrorStatus } from '../../../utils/apiErrorText';
import type { Client } from '../../../types/project';

interface QuickAddClientDialogProps {
  open: boolean;
  onClose: () => void;
  /** Existing clients — used for the non-blocking duplicate-name warning. */
  existing: Client[];
  /** Called with the newly-created client so the caller can refresh + select it. */
  onCreated: (client: Client) => void;
}

export const QuickAddClientDialog = ({
  open, onClose, existing, onCreated,
}: QuickAddClientDialogProps) => {
  const [clientName, setClientName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) { setClientName(''); setError(''); setSubmitting(false); }
  }, [open]);

  const handleSubmit = async () => {
    const name = clientName.trim();
    if (!name) { setError('Client Name is required'); return; }
    setSubmitting(true);
    setError('');
    try {
      const created = await createClient({ ClientName: name });
      onCreated(created);
    } catch (err) {
      // BUG-2026-0135: show WHY it failed (e.g. 403 "Insufficient permissions
      // to create clients") instead of a generic message, and point refused
      // roles at the admin path so they aren't dead-ended mid-quote.
      const msg = apiErrorText(err, 'Failed to create client.');
      setError(apiErrorStatus(err) === 403
        ? `${msg}. Ask a lab admin to add the client, then select it here.`
        : msg);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Add New Client</DialogTitle>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2.5 }}>
        <TextField
          size="small"
          label="Client Name"
          required
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter' && !submitting) handleSubmit(); }}
        />
        <DuplicateWarning
          value={clientName}
          candidates={existing}
          nameSelector={(c) => c.ClientName}
          label="Existing clients with similar names:"
        />
        <Typography variant="caption" color="text.secondary">
          You can add contact, email, and phone later on the Clients page.
        </Typography>
        {error && <Typography variant="body2" color="error">{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 600, px: 3, borderRadius: 1.5 }}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Create Client
        </Button>
      </DialogActions>
    </Dialog>
  );
};
